import React, { useState, useEffect, useCallback, useRef } from 'react';
import socket, { connectWithAuth } from './socket';
import { playNotificationSound } from './sounds';
import Lobby from './components/Lobby';
import ChatThread from './components/ChatThread';
import UserSetup from './components/UserSetup';
import OnboardingTour from './components/OnboardingTour';
import SearchPanel from './components/SearchPanel';
import AppShell, { DetailEmpty } from './components/AppShell';
import WorkRail from './components/WorkRail';
import useBreakpoint from './hooks/useBreakpoint';
import { track, identifyUser, resetAnalytics } from './analytics';
import './App.css';

function App() {
  // Direction B multi-device: phone keeps the shipped single-column swap;
  // tablet/desktop compose the same components into panes (see AppShell).
  const breakpoint = useBreakpoint();
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(
    () => localStorage.getItem('symphonate-workspace') || null,
  );
  const [currentThread, setCurrentThread] = useState(null);
  const [rooms, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  // All signed-up members of the current workspace (not just those currently in
  // the room) so they can be @mentioned/tagged in the composer.
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [todos, setTodos] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [shortLists, setShortLists] = useState([]);
  const [roomLastSeen, setThreadLastSeen] = useState(null); // last_seen timestamp for current room
  const [sessionRestoring, setSessionRestoring] = useState(true); // true while checking stored session
  const [lastSeenCounts, setLastSeenCounts] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('symphonate-seen-counts') || '{}');
    } catch {
      return {};
    }
  });
  // Chat-message count the user had already seen in the current room when
  // they entered. Lets ChatThread land on the first unread message instead of
  // the top/bottom on rejoin. Null on reconnect so we don't re-scroll mid-chat.
  const [roomEntrySeenCount, setThreadEntrySeenCount] = useState(null);

  const [showSearch, setShowSearch] = useState(false);
  // The Lobby's create-workspace panel is open (the tour waits for it).
  const [workspaceSetupOpen, setWorkspaceSetupOpen] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  // Cross-workspace notifications (the global bell next to the username).
  const [allNotifications, setAllNotifications] = useState([]);
  const [allUnreadCount, setAllUnreadCount] = useState(0);
  // Per-user AI routing preferences: which AI answers generic asks
  // (primaryAiId, null = platform default Aimy) and which AIs are muted.
  // Persisted server-side in user_preferences so they follow the account
  // across devices; the server applies them when routing AI turns.
  const [aiPrefs, setAiPrefs] = useState({ primaryAiId: null, mutedAiIds: [] });
  // Deep-link target from an 'open-thread' window event (native push taps,
  // docs/mobile-apps.md §4). Held here until the session + socket are ready —
  // a cold-start push tap fires before login restore completes.
  const [pendingOpenThread, setPendingOpenThread] = useState(null);

  // Refs for reconnection (avoids stale closures in socket handlers)
  const currentThreadRef = useRef(null);
  const userRef = useRef(null);
  const lastSeenCountsRef = useRef(lastSeenCounts);
  // Mirrors `decisions` so the socket handler (registered once) can diff the
  // incoming list against the previously-known set without stale closure.
  const decisionsRef = useRef([]);
  // Tracks whether we have an outstanding browser-history entry pushed for the
  // currently-open thread, so the in-app "Leave" button and the browser Back
  // button keep the history stack in sync (see the in-app Back-button effects).
  const didPushThreadHistoryRef = useRef(false);

  useEffect(() => {
    decisionsRef.current = decisions;
  }, [decisions]);
  useEffect(() => {
    currentThreadRef.current = currentThread;
  }, [currentThread]);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    lastSeenCountsRef.current = lastSeenCounts;
  }, [lastSeenCounts]);

  // --- In-app Back-button navigation ---------------------------------------
  // The app is a single page that swaps views from state (Lobby ↔ ChatThread)
  // rather than a router, so without history integration the browser Back
  // button would leave the site entirely. We push a history entry when a
  // thread is opened from the lobby; the Back button then fires `popstate`,
  // which returns to the lobby in-app instead of navigating away.
  useEffect(() => {
    if (!currentThread) return;
    // Only push once per lobby→thread transition. Switching directly between
    // threads (already inside one) reuses the single entry, so Back always
    // returns to the lobby.
    if (!didPushThreadHistoryRef.current) {
      window.history.pushState({ symphonateThread: currentThread.id }, '');
      didPushThreadHistoryRef.current = true;
    }
  }, [currentThread]);

  useEffect(() => {
    const onPopState = () => {
      // Back button while viewing a thread → return to the lobby. The browser
      // has already consumed our pushed entry, so clear the flag before the
      // teardown (which would otherwise call history.back() a second time).
      if (currentThreadRef.current) {
        didPushThreadHistoryRef.current = false;
        handleLeaveThread();
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
    // handleLeaveThread is stable (useCallback []), so this binds once.
  }, []);

  // Keep the seen-count for the thread the user is actively viewing in sync
  // with the messages on screen, counting only real chat messages — never the
  // ephemeral "X joined the room" / "X left the room" presence notices. This
  // means stepping into a thread and back out never leaves a phantom unread
  // badge, and presence churn (people joining/leaving) never inflates it.
  useEffect(() => {
    if (!currentThread) return;
    const chatCount = messages.filter((m) => m.senderType !== 'system').length;
    setLastSeenCounts((prev) => {
      if ((prev[currentThread.id] || 0) >= chatCount) return prev;
      const updated = { ...prev, [currentThread.id]: chatCount };
      localStorage.setItem('symphonate-seen-counts', JSON.stringify(updated));
      return updated;
    });
  }, [messages, currentThread]);

  // Global keyboard shortcut: Ctrl+K / Cmd+K to toggle search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch((v) => !v);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Load the signed-in user's AI routing preferences (primary AI + mutes).
  useEffect(() => {
    if (!user?.id) return;
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
    const token = localStorage.getItem('symphonate-token');
    if (!token) return;
    fetch(`${SERVER_URL}/api/me/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.preferences) {
          setAiPrefs({
            primaryAiId: data.preferences.primaryAiId || null,
            mutedAiIds: data.preferences.mutedAiIds || [],
          });
        }
      })
      .catch(() => {
        /* fall back to platform defaults */
      });
  }, [user?.id]);

  // Load the current workspace's full member roster so any signed-up member can
  // be @mentioned in the composer, not only those currently joined to the room.
  useEffect(() => {
    if (!user?.id || !currentWorkspaceId) {
      setWorkspaceMembers([]);
      return;
    }
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
    const token = localStorage.getItem('symphonate-token');
    if (!token) return;
    let cancelled = false;
    fetch(`${SERVER_URL}/api/workspaces/${currentWorkspaceId}/members`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.members) setWorkspaceMembers(data.members);
      })
      .catch(() => {
        /* mention list falls back to live room participants */
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, currentWorkspaceId]);

  // Persist an AI-preference patch. Optimistic: local state is updated by the
  // caller first; a failed PATCH just means the next login re-syncs from the
  // server.
  const patchAIPrefs = useCallback((patch) => {
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
    const token = localStorage.getItem('symphonate-token');
    fetch(`${SERVER_URL}/api/me/preferences`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(patch),
    }).catch(() => {});
  }, []);

  const handleSetPrimaryAI = useCallback(
    (aiId) => {
      setAiPrefs((prev) => ({ ...prev, primaryAiId: aiId }));
      patchAIPrefs({ primaryAiId: aiId });
    },
    [patchAIPrefs],
  );

  const handleToggleMuteAI = useCallback(
    (aiId) => {
      const muted = aiPrefs.mutedAiIds.includes(aiId)
        ? aiPrefs.mutedAiIds.filter((id) => id !== aiId)
        : [...aiPrefs.mutedAiIds, aiId];
      setAiPrefs((prev) => ({ ...prev, mutedAiIds: muted }));
      patchAIPrefs({ mutedAiIds: muted });
    },
    [aiPrefs.mutedAiIds, patchAIPrefs],
  );

  // Fetch online users and notifications when workspace changes or connection established
  useEffect(() => {
    if (!connected || !currentWorkspaceId) return;
    socket.emit('get-online-users', { workspaceId: currentWorkspaceId }, (users) => {
      if (users) setOnlineUsers(users);
    });
    socket.emit('get-notifications', { workspaceId: currentWorkspaceId }, (res) => {
      if (res?.success) {
        setNotifications(res.notifications);
        setUnreadNotificationCount(res.unreadCount);
      }
    });
  }, [connected, currentWorkspaceId]);

  // Cross-workspace notifications for the global bell next to the username.
  // Workspace-independent, so it only depends on the connection (a switch
  // doesn't need to refetch — the live `notification` event keeps it current).
  useEffect(() => {
    if (!connected) return;
    socket.emit('get-all-notifications', {}, (res) => {
      if (res?.success) {
        setAllNotifications(res.notifications);
        setAllUnreadCount(res.unreadCount);
      }
    });
  }, [connected]);

  // Heartbeat: send every 30s to keep presence alive
  useEffect(() => {
    if (!connected) return;
    const interval = setInterval(() => {
      socket.emit('heartbeat');
    }, 30000);
    return () => clearInterval(interval);
  }, [connected]);

  // When the page comes back to the foreground (tab focused, phone unlocked,
  // OS resumed the app) immediately try to reconnect the socket. Mobile
  // browsers freeze socket.io's reconnect timers while the page is hidden,
  // which is the root cause of the "frozen page with red dot" we'd see after
  // a phone lock — the auto-reconnect loop simply never wakes up.
  useEffect(() => {
    const wake = () => {
      if (document.visibilityState !== 'visible') {
        // Backgrounded (app switch, phone lock): tell the server we're away
        // right now. Native push only fans out to offline/away users, so
        // without this a just-backgrounded phone stays 'online' until the
        // socket times out and misses the pushes it most needs. The 5-minute
        // idle timer can't cover this — its JS is frozen while hidden.
        if (socket.connected) socket.emit('status-change', { status: 'away' });
        return;
      }
      if (!socket.connected) socket.connect();
      else {
        socket.emit('heartbeat');
        socket.emit('status-change', { status: 'online' });
      }
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    return () => {
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
      window.removeEventListener('online', wake);
    };
  }, []);

  // Deep-link entry point: an 'open-thread' CustomEvent with
  // detail { roomId, workspaceId? } navigates into that thread (switching
  // workspace first when needed). Dispatched by the native push-tap handler
  // (docs/mobile-apps.md §4); usable by any other integration that wants to
  // drive navigation. The event just records the target — the effect after
  // handleLeaveThread consumes it once the session and socket are ready.
  useEffect(() => {
    const onOpenThread = (e) => {
      if (e?.detail?.roomId) setPendingOpenThread(e.detail);
    };
    window.addEventListener('open-thread', onOpenThread);
    return () => window.removeEventListener('open-thread', onOpenThread);
  }, []);

  // Idle detection: mark away after 5 minutes of no input
  useEffect(() => {
    if (!connected) return;
    let idleTimer;
    let isIdle = false;

    const resetIdle = () => {
      if (isIdle) {
        isIdle = false;
        socket.emit('status-change', { status: 'online' });
      }
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        isIdle = true;
        socket.emit('status-change', { status: 'away' });
      }, 300000); // 5 minutes
    };

    resetIdle();
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('keydown', resetIdle);
    window.addEventListener('touchstart', resetIdle);

    return () => {
      clearTimeout(idleTimer);
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
    };
  }, [connected]);

  // Session restoration: check localStorage for saved token on mount
  useEffect(() => {
    const token = localStorage.getItem('symphonate-token');
    const savedUser = localStorage.getItem('symphonate-user');
    if (token && savedUser) {
      const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
      fetch(`${SERVER_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => {
          if (!r.ok) throw new Error('Invalid token');
          return r.json();
        })
        .then((data) => {
          const restored = { ...data.user, token };
          setUser(restored);
          const ws = data.workspaces || [];
          setWorkspaces(ws);
          // Restore the previously-selected workspace if the user is still a
          // member; otherwise fall back to the first one returned by the server.
          const stored = localStorage.getItem('symphonate-workspace');
          const initialWs = (stored && ws.find((w) => w.id === stored)?.id) || ws[0]?.id || null;
          setCurrentWorkspaceId(initialWs);
          if (initialWs) localStorage.setItem('symphonate-workspace', initialWs);
          connectWithAuth(token);
          fetch(`${SERVER_URL}/api/rooms`)
            .then((r) => r.json())
            .then(setThreads)
            .catch(() => {});

          // Auto-rejoin last room if stored
          const lastThreadId = localStorage.getItem('symphonate-last-room');
          if (lastThreadId) {
            // Small delay to ensure socket connects first
            const onConnect = () => {
              socket.emit('join-thread', { roomId: lastThreadId, user: restored }, (response) => {
                if (response.success) {
                  setCurrentThread(response.room);
                  setMessages(response.messages || []);
                  setParticipants(response.room.participants || []);
                  setArtifacts(response.artifacts || []);
                  setTodos(response.todos || []);
                  setDecisions(response.decisions || []);
                  setShortLists(response.shortLists || []);
                  setThreadLastSeen(response.lastSeen || null);
                }
              });
              socket.off('connect', onConnect);
            };
            if (socket.connected) {
              onConnect();
            } else {
              socket.on('connect', onConnect);
            }
          }
          setSessionRestoring(false);
        })
        .catch(() => {
          // Token invalid — clear and show login
          localStorage.removeItem('symphonate-token');
          localStorage.removeItem('symphonate-user');
          setSessionRestoring(false);
        });
    } else {
      setSessionRestoring(false);
    }
  }, []);

  useEffect(() => {
    socket.on('presence-updated', ({ userId, status }) => {
      setOnlineUsers((prev) => {
        if (status === 'offline') {
          const next = { ...prev };
          delete next[userId];
          return next;
        }
        return { ...prev, [userId]: { status, lastSeen: Date.now() } };
      });
    });

    socket.on('notification', (notif) => {
      setNotifications((prev) => [notif, ...prev].slice(0, 50));
      setUnreadNotificationCount((prev) => prev + 1);
      // The global bell spans every workspace, so it always gets the new one.
      setAllNotifications((prev) => [notif, ...prev].slice(0, 50));
      setAllUnreadCount((prev) => prev + 1);
      playNotificationSound('mention');
    });

    socket.on('connect', () => {
      setConnected(true);
      // Re-join room on reconnection
      const room = currentThreadRef.current;
      const u = userRef.current;
      if (room && u) {
        socket.emit('join-thread', { roomId: room.id, user: u }, (response) => {
          if (response.success) {
            setMessages(response.messages || []);
            setParticipants(response.room.participants || []);
            setArtifacts(response.artifacts || []);
            setTodos(response.todos || []);
            setDecisions(response.decisions || []);
            setShortLists(response.shortLists || []);
            // Update seen count on reconnect — use chatMessageCount so
            // join/leave system messages never inflate the unread badge.
            const count = response.room.chatMessageCount ?? response.room.messageCount ?? 0;
            setLastSeenCounts((prev) => {
              const updated = { ...prev, [room.id]: count };
              localStorage.setItem('symphonate-seen-counts', JSON.stringify(updated));
              return updated;
            });
          } else {
            // Thread was cleaned up while disconnected
            setCurrentThread(null);
            setMessages([]);
            setParticipants([]);
            setArtifacts([]);
            setTodos([]);
            setDecisions([]);
            setShortLists([]);
          }
        });
      }
    });

    socket.on('disconnect', () => {
      setConnected(false);
      setOnlineUsers({});
    });
    socket.on('threads-updated', (updatedThreads) => {
      setThreads(updatedThreads);
      // Keep the open thread's AI roster fresh — workspace AI settings
      // changes (primary AI, disabled AIs) re-broadcast the thread list, and
      // the in-room sidebar/composer should reflect them without a rejoin.
      setCurrentThread((prev) => {
        if (!prev) return prev;
        const updated = (updatedThreads || []).find((r) => r.id === prev.id);
        return updated
          ? { ...prev, aiParticipants: updated.aiParticipants, icon: updated.icon }
          : prev;
      });
    });

    socket.on('message', (msg) => {
      setMessages((prev) => {
        // Deduplicate — the same message ID may arrive via both
        // 'message' event and history on rejoin
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });

      // System messages (joined / left the room) don't get a sound — the
      // presence dots already convey attention status, and we don't want to
      // alert users every time someone backgrounds the app.
      if (msg.senderType === 'system') return;

      // Play notification sounds for chat messages from others
      const u = userRef.current;
      if (u && msg.senderId !== u.id) {
        if (msg.content.toLowerCase().includes('@' + (u.name || '').toLowerCase())) {
          playNotificationSound('mention');
        } else {
          playNotificationSound('message');
        }
      }
    });

    socket.on('participants-updated', (updatedParticipants) => {
      setParticipants(updatedParticipants);
    });

    socket.on('artifacts-updated', (updatedArtifacts) => {
      setArtifacts(updatedArtifacts);
    });

    socket.on('todos-updated', (updatedTodos) => {
      setTodos(updatedTodos);
    });

    socket.on('decisions-updated', (updatedDecisions) => {
      // Fire 'decision-logged' once for each decision that newly appears —
      // whether a human logged it or the AI created it via a [DECISION:] tag.
      // Diffing against the prior set means historical decisions loaded on
      // join never fire, and removals/status-changes don't either. This is
      // the activation "aha" (gtm-playbook §5/§11).
      const knownIds = new Set((decisionsRef.current || []).map((d) => d.id));
      const room = currentThreadRef.current;
      for (const d of updatedDecisions || []) {
        if (d && !knownIds.has(d.id)) {
          track('decision-logged', {
            roomId: room?.id || null,
            workspaceId: room?.workspaceId || null,
          });
        }
      }
      setDecisions(updatedDecisions);
    });

    socket.on('short-lists-updated', (updatedShortLists) => {
      setShortLists(updatedShortLists || []);
    });

    socket.on('reaction-updated', ({ messageId, reactions }) => {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, reactions } : msg)),
      );
    });

    socket.on('message-edited', ({ messageId, content, edited }) => {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, content, edited } : msg)),
      );
    });

    socket.on('message-deleted', ({ messageId }) => {
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    });

    socket.on('message-pinned', ({ messageId, pinned }) => {
      setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, pinned } : msg)));
    });

    socket.on('thread-deleted', () => {
      setCurrentThread(null);
      setMessages([]);
      setParticipants([]);
      setArtifacts([]);
      setTodos([]);
    });

    socket.on('thread-objective-updated', ({ roomId, objective }) => {
      setCurrentThread((prev) => (prev && prev.id === roomId ? { ...prev, objective } : prev));
    });

    socket.on('thread-icon-updated', ({ roomId, icon }) => {
      setCurrentThread((prev) => (prev && prev.id === roomId ? { ...prev, icon } : prev));
    });

    // An admin blocked (or removed) this user from a workspace, or restored
    // them. Drop the workspace from the switcher immediately and leave any of
    // its threads rather than waiting for a reload — the server has already
    // stopped honouring events for it.
    socket.on('workspace-access-changed', ({ workspaceId, hasAccess }) => {
      if (hasAccess) {
        // Access restored (unblocked) — re-read the membership list.
        const token = localStorage.getItem('symphonate-token');
        fetch(`${process.env.REACT_APP_SERVER_URL || ''}/api/workspaces`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (data?.workspaces) setWorkspaces(data.workspaces);
          })
          .catch(() => {});
        return;
      }
      setWorkspaces((prev) => {
        const remaining = prev.filter((w) => w.id !== workspaceId);
        setCurrentWorkspaceId((current) => {
          if (current !== workspaceId) return current;
          const next = remaining[0]?.id || null;
          if (next) localStorage.setItem('symphonate-workspace', next);
          else localStorage.removeItem('symphonate-workspace');
          return next;
        });
        return remaining;
      });
      setCurrentThread((prev) => {
        if (!prev || prev.workspaceId !== workspaceId) return prev;
        setMessages([]);
        setParticipants([]);
        setArtifacts([]);
        setTodos([]);
        return null;
      });
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('threads-updated');
      socket.off('presence-updated');
      socket.off('message');
      socket.off('participants-updated');
      socket.off('artifacts-updated');
      socket.off('todos-updated');
      socket.off('decisions-updated');
      socket.off('short-lists-updated');
      socket.off('reaction-updated');
      socket.off('message-edited');
      socket.off('message-deleted');
      socket.off('message-pinned');
      socket.off('thread-deleted');
      socket.off('thread-objective-updated');
      socket.off('thread-icon-updated');
      socket.off('workspace-access-changed');
      socket.off('notification');
    };
  }, []);

  const handleUserSetup = useCallback((userData, authMode) => {
    setUser(userData);
    // Tie all subsequent product events to this user, then mark the entry
    // point. 'register' is the top of the activation funnel (gtm-playbook §11).
    identifyUser(userData);
    track(authMode === 'register' ? 'signup' : 'login');
    const ws = userData.workspaces || [];
    setWorkspaces(ws);
    const initialWs = ws[0]?.id || null;
    setCurrentWorkspaceId(initialWs);
    if (initialWs) localStorage.setItem('symphonate-workspace', initialWs);
    connectWithAuth(userData.token);
    // Fetch available rooms
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
    fetch(`${SERVER_URL}/api/rooms`)
      .then((r) => r.json())
      .then(setThreads)
      .catch(() => {});
  }, []);

  /**
   * If the URL carries an ?invite=CODE param, redeem it after auth: confirm
   * with the user, POST to /api/invites/:code/accept, then add the new
   * workspace to state and switch to it. Strips the param afterward so a
   * refresh doesn't re-prompt.
   */
  useEffect(() => {
    if (!user?.token) return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('invite');
    if (!code) return;
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
    let cancelled = false;
    (async () => {
      try {
        const previewRes = await fetch(`${SERVER_URL}/api/invites/${code}`);
        if (!previewRes.ok) {
          window.alert('This invite is invalid or no longer valid.');
          return;
        }
        const preview = await previewRes.json();
        const wsName = preview.workspace?.name || 'this workspace';
        if (!window.confirm(`Join ${wsName} as a ${preview.invite.role}?`)) return;
        const acceptRes = await fetch(`${SERVER_URL}/api/invites/${code}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`,
          },
        });
        if (!acceptRes.ok) {
          const data = await acceptRes.json().catch(() => ({}));
          window.alert(data.error || 'Could not accept invite.');
          return;
        }
        const data = await acceptRes.json();
        if (cancelled) return;
        // Add or update the workspace in local state and switch to it.
        setWorkspaces((prev) => {
          const without = prev.filter((w) => w.id !== data.workspace.id);
          return [...without, data.workspace];
        });
        setCurrentWorkspaceId(data.workspace.id);
        localStorage.setItem('symphonate-workspace', data.workspace.id);
      } catch {
        window.alert('Could not accept invite — please try again.');
      } finally {
        // Strip ?invite=... from the URL so refresh doesn't re-prompt.
        const url = new URL(window.location.href);
        url.searchParams.delete('invite');
        window.history.replaceState({}, '', url.toString());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.token]);

  const handleJoinThread = useCallback(
    (roomId) => {
      // Snapshot the seen-count from before this join — handleJoinThread is
      // memoized on [user] so reading the ref avoids a stale closure on
      // lastSeenCounts.
      const prevSeen = lastSeenCountsRef.current[roomId] || 0;
      socket.emit('join-thread', { roomId, user }, (response) => {
        if (response?.success) {
          setCurrentThread(response.room);
          setMessages(response.messages || []);
          setParticipants(response.room.participants || []);
          setArtifacts(response.artifacts || []);
          setTodos(response.todos || []);
          setDecisions(response.decisions || []);
          setShortLists(response.shortLists || []);
          setThreadLastSeen(response.lastSeen || null);
          localStorage.setItem('symphonate-last-room', roomId);
          const count = response.room.chatMessageCount ?? response.room.messageCount ?? 0;
          // ChatThread uses this to land on the first unread message.
          setThreadEntrySeenCount(prevSeen);
          setLastSeenCounts((prev) => {
            const updated = { ...prev, [roomId]: count };
            localStorage.setItem('symphonate-seen-counts', JSON.stringify(updated));
            return updated;
          });
        } else {
          setError(response?.error || "Couldn't open that thread. Try again.");
          setTimeout(() => setError(null), 4000);
        }
      });
    },
    [user],
  );

  const handleCreateThread = useCallback(
    (roomName, template) => {
      socket.emit(
        'create-thread',
        {
          roomName,
          user,
          template: template || 'freeform',
          workspaceId: currentWorkspaceId,
        },
        (response) => {
          if (response.success) {
            track('room-created', { template: template || 'freeform' });
            handleJoinThread(response.room.id);
          }
        },
      );
    },
    [user, currentWorkspaceId, handleJoinThread],
  );

  const handleSwitchWorkspace = useCallback((workspaceId) => {
    setCurrentWorkspaceId(workspaceId);
    if (workspaceId) localStorage.setItem('symphonate-workspace', workspaceId);
  }, []);

  const handleCreateWorkspace = useCallback(async (name, goal, options = {}) => {
    const token = localStorage.getItem('symphonate-token');
    const res = await fetch(`${process.env.REACT_APP_SERVER_URL || ''}/api/workspaces`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        name,
        goal: goal || undefined,
        seedSuggested: options.seedSuggested === true,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { error: err.error || 'Failed to create workspace' };
    }
    const data = await res.json();
    const ws = data.workspace;
    track('workspace-created', { goal: goal || null, invitees: options.inviteeCount || 0 });
    setWorkspaces((prev) => [...prev, ws]);
    setCurrentWorkspaceId(ws.id);
    localStorage.setItem('symphonate-workspace', ws.id);
    // Refresh the thread list so any seeded suggested threads (welcome +
    // goal-specific) show up immediately. The seeding happens server-side in
    // the REST handler and isn't broadcast, so we re-fetch the full list.
    fetch(`${process.env.REACT_APP_SERVER_URL || ''}/api/rooms`)
      .then((r) => (r.ok ? r.json() : null))
      .then((allThreads) => {
        if (allThreads) setThreads(allThreads);
      })
      .catch(() => {});
    // The create panel goes on to invite the team into it.
    return { workspace: ws };
  }, []);

  const handleDeleteWorkspace = useCallback(
    async (workspaceId) => {
      const token = localStorage.getItem('symphonate-token');
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL || ''}/api/workspaces/${workspaceId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { error: err.error || 'Failed to delete workspace' };
      }
      setWorkspaces((prev) => prev.filter((w) => w.id !== workspaceId));
      setCurrentWorkspaceId((current) => {
        if (current !== workspaceId) return current;
        // The deleted workspace was active — switch to any remaining one.
        const remaining = workspaces.filter((w) => w.id !== workspaceId);
        const next = remaining[0]?.id || null;
        if (next) localStorage.setItem('symphonate-workspace', next);
        else localStorage.removeItem('symphonate-workspace');
        return next;
      });
      // Server nulls out workspace_id on the deleted workspace's rooms; refresh
      // the room list so the lobby reflects the new state.
      fetch(`${process.env.REACT_APP_SERVER_URL || ''}/api/rooms`)
        .then((r) => (r.ok ? r.json() : null))
        .then((allThreads) => {
          if (allThreads) setThreads(allThreads);
        })
        .catch(() => {});
      return { ok: true };
    },
    [workspaces],
  );

  const handleLeaveWorkspace = useCallback(
    async (workspaceId) => {
      const token = localStorage.getItem('symphonate-token');
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL || ''}/api/workspaces/${workspaceId}/leave`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { error: err.error || 'Failed to leave workspace' };
      }
      setWorkspaces((prev) => prev.filter((w) => w.id !== workspaceId));
      setCurrentWorkspaceId((current) => {
        if (current !== workspaceId) return current;
        // The left workspace was active — switch to any remaining one.
        const remaining = workspaces.filter((w) => w.id !== workspaceId);
        const next = remaining[0]?.id || null;
        if (next) localStorage.setItem('symphonate-workspace', next);
        else localStorage.removeItem('symphonate-workspace');
        return next;
      });
      // The lobby's room list is workspace-scoped; refresh it so threads from
      // the workspace we just left drop away.
      fetch(`${process.env.REACT_APP_SERVER_URL || ''}/api/rooms`)
        .then((r) => (r.ok ? r.json() : null))
        .then((allThreads) => {
          if (allThreads) setThreads(allThreads);
        })
        .catch(() => {});
      return { ok: true };
    },
    [workspaces],
  );

  const handleLeaveThread = useCallback(() => {
    socket.emit('leave-thread');
    setCurrentThread(null);
    // Update the ref synchronously so the `popstate` handler (below) sees the
    // thread as already closed and treats the history.back() call here as a
    // no-op rather than tearing down twice.
    currentThreadRef.current = null;
    setMessages([]);
    setParticipants([]);
    setArtifacts([]);
    setTodos([]);
    setDecisions([]);
    setShortLists([]);
    setThreadLastSeen(null);
    localStorage.removeItem('symphonate-last-room');
    // Pop the history entry we pushed when opening the thread so the browser
    // Back button stays in sync with the in-app view (no extra press needed).
    // When this leave was itself triggered by Back, the flag is already false
    // and the entry consumed — so we don't double-pop.
    if (didPushThreadHistoryRef.current) {
      didPushThreadHistoryRef.current = false;
      window.history.back();
    }
  }, []);

  // Consume a pending 'open-thread' deep link once the session is restored and
  // the socket is up. Joining by roomId works regardless of the client's
  // current workspace; switching currentWorkspaceId keeps the Lobby coherent
  // when the user backs out of the thread.
  useEffect(() => {
    if (!pendingOpenThread || !user || !connected) return;
    const { roomId, workspaceId } = pendingOpenThread;
    setPendingOpenThread(null);
    if (currentThreadRef.current?.id === roomId) return; // already there
    if (workspaceId && workspaceId !== currentWorkspaceId) handleSwitchWorkspace(workspaceId);
    if (currentThreadRef.current) handleLeaveThread();
    // Same leave→join settling delay the SearchPanel navigation uses.
    setTimeout(() => handleJoinThread(roomId), 100);
  }, [
    pendingOpenThread,
    user,
    connected,
    currentWorkspaceId,
    handleSwitchWorkspace,
    handleLeaveThread,
    handleJoinThread,
  ]);

  const handleDeleteThread = useCallback(
    (roomId) => {
      socket.emit('delete-thread', { roomId }, (response) => {
        if (response?.success) {
          // If we're in the deleted room, go back to lobby
          if (currentThread && currentThread.id === roomId) {
            setCurrentThread(null);
            currentThreadRef.current = null;
            setMessages([]);
            setParticipants([]);
            setArtifacts([]);
            setTodos([]);
            setDecisions([]);
            setShortLists([]);
            // Keep the browser-history entry in sync with the in-app view.
            if (didPushThreadHistoryRef.current) {
              didPushThreadHistoryRef.current = false;
              window.history.back();
            }
          }
        } else {
          // Surface the failure instead of silently doing nothing — otherwise a
          // rejected/failed delete looks identical to a broken button.
          const reason =
            response?.error ||
            'No response from the server (it may need to reconnect). Please try again.';
          console.error('Delete thread failed:', reason);
          window.alert(`Could not delete the thread: ${reason}`);
        }
      });
    },
    [currentThread],
  );

  const handleSendMessage = useCallback(
    (content, replyTo, attachmentIds, options = {}) => {
      if (!currentThread) return;
      track('message-sent', { hasAttachments: (attachmentIds || []).length > 0 });
      socket.emit(
        'send-message',
        {
          roomId: currentThread.id,
          content,
          replyTo: replyTo || null,
          attachmentIds: attachmentIds || [],
          skipAITrigger: !!options.skipAITrigger,
          intent: options.intent || null,
        },
        (response) => {
          if (response?.error) {
            setError(response.error);
            setTimeout(() => setError(null), 4000);
          }
        },
      );
    },
    [currentThread],
  );

  const handleEditMessage = useCallback(
    (messageId, content) => {
      if (!currentThread) return;
      socket.emit('edit-message', { roomId: currentThread.id, messageId, content });
    },
    [currentThread],
  );

  const handleDeleteMessage = useCallback(
    (messageId) => {
      if (!currentThread) return;
      socket.emit('delete-message', { roomId: currentThread.id, messageId });
    },
    [currentThread],
  );

  const handleTogglePin = useCallback(
    (messageId) => {
      if (!currentThread) return;
      socket.emit('toggle-pin', { roomId: currentThread.id, messageId });
    },
    [currentThread],
  );

  const handleUpdateObjective = useCallback(
    (objective) =>
      new Promise((resolve) => {
        if (!currentThread) return resolve({ error: 'No room' });
        socket.emit(
          'update-thread-objective',
          { roomId: currentThread.id, objective },
          (response) => {
            if (response?.error) {
              setError(response.error);
              setTimeout(() => setError(null), 4000);
            }
            resolve(response || {});
          },
        );
      }),
    [currentThread],
  );

  const handleUpdateThreadIcon = useCallback(
    (icon) => {
      if (!currentThread) return;
      socket.emit('update-thread-icon', { roomId: currentThread.id, icon }, (response) => {
        if (response?.error) {
          setError(response.error);
          setTimeout(() => setError(null), 4000);
        }
      });
    },
    [currentThread],
  );

  const handleUpdateWorkspaceIcon = useCallback(async (workspaceId, icon) => {
    const token = localStorage.getItem('symphonate-token');
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL || ''}/api/workspaces/${workspaceId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ icon }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setError(err.error || 'Failed to update workspace icon');
      setTimeout(() => setError(null), 4000);
      return;
    }
    const data = await res.json();
    const updated = data.workspace;
    setWorkspaces((prev) => prev.map((w) => (w.id === workspaceId ? { ...w, ...updated } : w)));
  }, []);

  /**
   * Merge a partial workspace update (e.g. the member-permissions toggle) into
   * local state so every gate that reads the workspace re-evaluates at once.
   */
  const handleWorkspaceUpdated = useCallback((workspaceId, patch) => {
    setWorkspaces((prev) => prev.map((w) => (w.id === workspaceId ? { ...w, ...patch } : w)));
  }, []);

  const handleRenameWorkspace = useCallback(async (workspaceId, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    const token = localStorage.getItem('symphonate-token');
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL || ''}/api/workspaces/${workspaceId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: trimmed }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setError(err.error || 'Failed to rename workspace');
      setTimeout(() => setError(null), 4000);
      return;
    }
    const data = await res.json();
    const updated = data.workspace;
    setWorkspaces((prev) => prev.map((w) => (w.id === workspaceId ? { ...w, ...updated } : w)));
  }, []);

  const handleSummarize = useCallback(
    (since) => {
      if (!currentThread) return;
      socket.emit(
        'summarize-chat',
        { roomId: currentThread.id, since: since || roomLastSeen || null },
        (response) => {
          if (response?.error) {
            setError(response.error);
            setTimeout(() => setError(null), 4000);
          }
        },
      );
    },
    [currentThread, roomLastSeen],
  );

  const handleInvokeAI = useCallback(
    (focusMessageId) => {
      if (!currentThread) return;
      // Fan the explicit invocation out to every AI in the room (Aimy +
      // Aiden when Gemini is configured). The server serialises the calls
      // so the second AI sees the first's reply.
      track('ai-invoked', { explicit: true });
      socket.emit('invoke-ai', {
        roomId: currentThread.id,
        invokeAll: true,
        focusMessageId: focusMessageId || undefined,
      });
    },
    [currentThread],
  );

  const handleAddArtifact = useCallback(
    ({ title, content, type }) => {
      if (!currentThread) return;
      socket.emit('add-artifact', { roomId: currentThread.id, title, content, type });
    },
    [currentThread],
  );

  const handleRemoveArtifact = useCallback(
    (artifactId) => {
      if (!currentThread) return;
      socket.emit('remove-artifact', { roomId: currentThread.id, artifactId });
    },
    [currentThread],
  );

  const handleUpdateArtifact = useCallback(
    (artifactId, updates) => {
      if (!currentThread) return;
      socket.emit(
        'update-artifact',
        { roomId: currentThread.id, artifactId, ...updates },
        (response) => {
          if (response?.error) {
            setError(response.error);
            setTimeout(() => setError(null), 4000);
          }
        },
      );
    },
    [currentThread],
  );

  const handleGetArtifactVersions = useCallback((artifactId, callback) => {
    socket.emit('get-artifact-versions', { artifactId }, callback);
  }, []);

  const handleVotePoll = useCallback(
    (artifactId, optionIndex) => {
      if (!currentThread) return;
      socket.emit(
        'vote-poll',
        { roomId: currentThread.id, artifactId, optionIndex },
        (response) => {
          if (response?.error) {
            setError(response.error);
            setTimeout(() => setError(null), 4000);
          }
        },
      );
    },
    [currentThread],
  );

  const handleClosePoll = useCallback(
    (artifactId, closed) => {
      if (!currentThread) return;
      socket.emit('close-poll', { roomId: currentThread.id, artifactId, closed }, (response) => {
        if (response?.error) {
          setError(response.error);
          setTimeout(() => setError(null), 4000);
        }
      });
    },
    [currentThread],
  );

  const handleToggleChecklistItem = useCallback(
    (artifactId, lineIndex) => {
      if (!currentThread) return;
      socket.emit(
        'toggle-checklist-item',
        { roomId: currentThread.id, artifactId, lineIndex },
        (response) => {
          if (response?.error) {
            setError(response.error);
            setTimeout(() => setError(null), 4000);
          }
        },
      );
    },
    [currentThread],
  );

  const handleAddTodo = useCallback(
    (text) => {
      if (!currentThread) return;
      socket.emit('add-todo', { roomId: currentThread.id, text });
    },
    [currentThread],
  );

  const handleToggleTodo = useCallback(
    (todoId) => {
      if (!currentThread) return;
      socket.emit('toggle-todo', { roomId: currentThread.id, todoId });
    },
    [currentThread],
  );

  const handleRemoveTodo = useCallback(
    (todoId) => {
      if (!currentThread) return;
      socket.emit('remove-todo', { roomId: currentThread.id, todoId });
    },
    [currentThread],
  );

  const handleLogDecision = useCallback(
    (title, description) => {
      if (!currentThread) return;
      // Note: the 'decision-logged' analytics event is fired centrally in the
      // 'decisions-updated' handler (single source) so AI-created decisions
      // count too — see the decision-diff effect below.
      socket.emit('log-decision', { roomId: currentThread.id, title, description }, (response) => {
        if (response?.error) {
          setError(response.error);
          setTimeout(() => setError(null), 4000);
        }
      });
    },
    [currentThread],
  );

  const handleRemoveDecision = useCallback(
    (decisionId) => {
      if (!currentThread) return;
      socket.emit('remove-decision', { roomId: currentThread.id, decisionId });
    },
    [currentThread],
  );

  const handleUpdateDecisionStatus = useCallback(
    (decisionId, status) => {
      if (!currentThread) return;
      socket.emit('update-decision-status', {
        roomId: currentThread.id,
        decisionId,
        status,
      });
    },
    [currentThread],
  );

  const handleAddShortList = useCallback(
    (title, items) => {
      if (!currentThread) return;
      socket.emit('add-short-list', { roomId: currentThread.id, title, items });
    },
    [currentThread],
  );

  const handleAddShortListItem = useCallback(
    (listId, item) => {
      if (!currentThread) return;
      socket.emit('add-short-list-item', { roomId: currentThread.id, listId, item });
    },
    [currentThread],
  );

  const handleRemoveShortListItem = useCallback(
    (listId, itemId) => {
      if (!currentThread) return;
      socket.emit('remove-short-list-item', { roomId: currentThread.id, listId, itemId });
    },
    [currentThread],
  );

  const handleToggleShortListItemPin = useCallback(
    (listId, itemId) => {
      if (!currentThread) return;
      socket.emit('toggle-short-list-item-pin', { roomId: currentThread.id, listId, itemId });
    },
    [currentThread],
  );

  const handleRemoveShortList = useCallback(
    (listId) => {
      if (!currentThread) return;
      socket.emit('remove-short-list', { roomId: currentThread.id, listId });
    },
    [currentThread],
  );

  const handleToggleReaction = useCallback(
    (messageId, emoji) => {
      if (!currentThread) return;
      socket.emit('toggle-reaction', { roomId: currentThread.id, messageId, emoji });
    },
    [currentThread],
  );

  const handleMarkNotificationRead = useCallback((notificationId) => {
    socket.emit('mark-notification-read', { notificationId }, (res) => {
      if (res?.success) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read: 1 } : n)),
        );
        setUnreadNotificationCount((prev) => Math.max(0, prev - 1));
      }
    });
  }, []);

  const handleMarkAllNotificationsRead = useCallback(() => {
    if (!currentWorkspaceId) return;
    socket.emit('mark-all-notifications-read', { workspaceId: currentWorkspaceId }, (res) => {
      if (res?.success) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: 1 })));
        setUnreadNotificationCount(0);
      }
    });
  }, [currentWorkspaceId]);

  // --- Global (cross-workspace) notification bell handlers ---
  // The global bell passes the whole notification (not just an id) so we can
  // tell which workspace it belongs to and keep the current-workspace bell in
  // sync when they overlap.
  const handleMarkGlobalNotificationRead = useCallback(
    (notif) => {
      const id = notif.id;
      socket.emit('mark-notification-read', { notificationId: id }, (res) => {
        if (!res?.success) return;
        setAllNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: 1 } : n)));
        setAllUnreadCount((prev) => Math.max(0, prev - 1));
        const wsId = notif.workspaceId || notif.workspace_id;
        if (wsId === currentWorkspaceId) {
          setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: 1 } : n)));
          setUnreadNotificationCount((prev) => Math.max(0, prev - 1));
        }
      });
    },
    [currentWorkspaceId],
  );

  const handleMarkAllGlobalNotificationsRead = useCallback(() => {
    socket.emit('mark-all-notifications-read', {}, (res) => {
      if (!res?.success) return;
      setAllNotifications((prev) => prev.map((n) => ({ ...n, read: 1 })));
      setAllUnreadCount(0);
      // Everything is read now, so the current-workspace bell is too.
      setNotifications((prev) => prev.map((n) => ({ ...n, read: 1 })));
      setUnreadNotificationCount(0);
    });
  }, []);

  const handleNavigateGlobalNotification = useCallback(
    (notif) => {
      const roomId = notif.roomId || notif.room_id;
      const wsId = notif.workspaceId || notif.workspace_id;
      if (!roomId) return;
      if (wsId && wsId !== currentWorkspaceId) {
        // Reuse the deep-link path: switch workspace, then join the thread.
        setPendingOpenThread({ roomId, workspaceId: wsId });
      } else {
        handleJoinThread(roomId);
      }
    },
    [currentWorkspaceId, handleJoinThread],
  );

  // Update the signed-in user's own display name / avatar. Persists via REST,
  // mirrors the change into local state + localStorage, then refreshes the
  // live socket so `socket.user` (used for outgoing messages and room
  // participant entries) reflects the new identity. Throws on failure so the
  // caller can surface the error inline.
  const handleUpdateProfile = useCallback(async ({ name, avatar }) => {
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
    const token = localStorage.getItem('symphonate-token');
    const res = await fetch(`${SERVER_URL}/api/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name, avatar }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Could not update profile');
    }
    const fresh = data.user;
    setUser((prev) => ({ ...prev, name: fresh.name, avatar: fresh.avatar }));
    localStorage.setItem(
      'symphonate-user',
      JSON.stringify({ id: fresh.id, email: fresh.email, name: fresh.name, avatar: fresh.avatar }),
    );
    // Reconnect so the auth middleware reloads the user from the DB; the
    // existing connect handler re-joins the current room (if any). Guarded on
    // `connected` so we don't kick an already-reconnecting socket.
    if (socket.connected) {
      socket.disconnect();
      socket.connect();
    }
    return fresh;
  }, []);

  const handleLogout = useCallback(() => {
    resetAnalytics();
    socket.disconnect();
    localStorage.removeItem('symphonate-token');
    localStorage.removeItem('symphonate-user');
    localStorage.removeItem('symphonate-last-room');
    localStorage.removeItem('symphonate-workspace');
    window.sessionStorage.removeItem('symphonate-admin-token');
    setUser(null);
    setWorkspaces([]);
    setCurrentWorkspaceId(null);
    setCurrentThread(null);
    currentThreadRef.current = null;
    didPushThreadHistoryRef.current = false;
    setMessages([]);
    setParticipants([]);
    setArtifacts([]);
    setTodos([]);
    setDecisions([]);
    setShortLists([]);
    setThreadLastSeen(null);
    setAiPrefs({ primaryAiId: null, mutedAiIds: [] });
    setConnected(false);
  }, []);

  if (sessionRestoring) {
    return (
      <div className="user-setup">
        <div className="setup-card" style={{ textAlign: 'center', padding: '48px' }}>
          <h1 style={{ marginBottom: '12px' }}>Symphonate</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Restoring your session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <UserSetup onSetup={handleUserSetup} />;
  }

  // Only show rooms from the currently selected workspace; legacy rooms
  // (no workspaceId) fall through to whatever workspace the user is in so
  // they're still reachable. With no workspace at all (a fresh signup) there
  // is nothing to show — never everyone else's threads.
  const visibleThreads = currentWorkspaceId
    ? rooms.filter((r) => !r.workspaceId || r.workspaceId === currentWorkspaceId)
    : [];
  // No workspace yet: the Lobby shows only the "create your workspace" step,
  // and the first-run tour waits until there's a workspace to tour (and until
  // the create panel — with its invite link — is closed).
  const hasWorkspace = workspaces.length > 0;
  const showTour = hasWorkspace && !workspaceSetupOpen;

  // Viewers are read-only, so the Work rail's lightbox hides its edit pencil
  // for them (the server enforces the same via requireWorkspaceRole('member')).
  const currentWorkspaceRole =
    workspaces.find((w) => w.id === currentWorkspaceId)?.role || 'member';
  const canEditWorkspaceContent = currentWorkspaceRole !== 'viewer';

  const lobbyEl = (
    <Lobby
      user={user}
      rooms={visibleThreads}
      connected={connected}
      workspaces={workspaces}
      currentWorkspaceId={currentWorkspaceId}
      activeThreadId={currentThread?.id || null}
      onSwitchWorkspace={handleSwitchWorkspace}
      onCreateWorkspace={handleCreateWorkspace}
      onDeleteWorkspace={handleDeleteWorkspace}
      onLeaveWorkspace={handleLeaveWorkspace}
      onUpdateWorkspaceIcon={handleUpdateWorkspaceIcon}
      onRenameWorkspace={handleRenameWorkspace}
      onWorkspaceUpdated={handleWorkspaceUpdated}
      onCreateThread={handleCreateThread}
      onJoinThread={handleJoinThread}
      onDeleteThread={handleDeleteThread}
      onLogout={handleLogout}
      onUpdateProfile={handleUpdateProfile}
      lastSeenCounts={lastSeenCounts}
      onlineUsers={onlineUsers}
      notifications={notifications}
      unreadNotificationCount={unreadNotificationCount}
      onMarkNotificationRead={handleMarkNotificationRead}
      onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
      allNotifications={allNotifications}
      allUnreadCount={allUnreadCount}
      onMarkGlobalNotificationRead={handleMarkGlobalNotificationRead}
      onMarkAllGlobalNotificationsRead={handleMarkAllGlobalNotificationsRead}
      onNavigateGlobalNotification={handleNavigateGlobalNotification}
      workspaceMembers={workspaceMembers}
      onWorkspaceSetupChange={setWorkspaceSetupOpen}
    />
  );

  const chatEl = currentThread ? (
    <ChatThread
      user={user}
      room={currentThread}
      messages={messages}
      participants={participants}
      workspaceMembers={workspaceMembers}
      connected={connected}
      error={error}
      artifacts={artifacts}
      todos={todos}
      decisions={decisions}
      shortLists={shortLists}
      onSendMessage={handleSendMessage}
      onLeaveThread={handleLeaveThread}
      onLogout={handleLogout}
      onInvokeAI={handleInvokeAI}
      onAddArtifact={handleAddArtifact}
      onRemoveArtifact={handleRemoveArtifact}
      onUpdateArtifact={handleUpdateArtifact}
      onGetArtifactVersions={handleGetArtifactVersions}
      onVotePoll={handleVotePoll}
      onClosePoll={handleClosePoll}
      onToggleChecklistItem={handleToggleChecklistItem}
      roomLastSeen={roomLastSeen}
      roomEntrySeenCount={roomEntrySeenCount}
      onAddTodo={handleAddTodo}
      onToggleTodo={handleToggleTodo}
      onRemoveTodo={handleRemoveTodo}
      onLogDecision={handleLogDecision}
      onRemoveDecision={handleRemoveDecision}
      onUpdateDecisionStatus={handleUpdateDecisionStatus}
      onAddShortList={handleAddShortList}
      onAddShortListItem={handleAddShortListItem}
      onRemoveShortListItem={handleRemoveShortListItem}
      onToggleShortListItemPin={handleToggleShortListItemPin}
      onRemoveShortList={handleRemoveShortList}
      onToggleReaction={handleToggleReaction}
      onEditMessage={handleEditMessage}
      onDeleteMessage={handleDeleteMessage}
      onTogglePin={handleTogglePin}
      onUpdateObjective={handleUpdateObjective}
      onUpdateIcon={handleUpdateThreadIcon}
      onSummarize={handleSummarize}
      socket={socket}
      onlineUsers={onlineUsers}
      notifications={notifications}
      unreadNotificationCount={unreadNotificationCount}
      onMarkNotificationRead={handleMarkNotificationRead}
      onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
      workspaceId={currentWorkspaceId}
      onSwitchThread={handleJoinThread}
      aiPrefs={aiPrefs}
      onToggleMuteAI={handleToggleMuteAI}
      onSetPrimaryAI={handleSetPrimaryAI}
      collapseRightPanel={breakpoint === 'desktop'}
    />
  ) : null;

  const searchEl =
    showSearch && currentWorkspaceId ? (
      <SearchPanel
        workspaceId={currentWorkspaceId}
        onNavigate={(roomId) => {
          handleLeaveThread();
          setTimeout(() => handleJoinThread(roomId), 100);
        }}
        onClose={() => setShowSearch(false)}
      />
    ) : null;

  // Phone (< 768px): the shipped single-column swap — Lobby OR ChatThread.
  // This path is intentionally byte-for-byte the original so the merged phone
  // design can't regress.
  if (breakpoint === 'phone') {
    if (!currentThread) {
      return (
        <>
          {lobbyEl}
          {searchEl}
          {showTour && <OnboardingTour userId={user.id} />}
        </>
      );
    }
    return (
      <>
        {chatEl}
        {searchEl}
      </>
    );
  }

  // A workspace-less account has nothing for the panes to hold — just the
  // single-column Lobby with its "create your workspace" step.
  if (!hasWorkspace) return lobbyEl;

  // Tablet / desktop: compose the same components into panes.
  return (
    <>
      <AppShell
        breakpoint={breakpoint}
        lobby={lobbyEl}
        detail={chatEl || <DetailEmpty />}
        workRail={
          <WorkRail
            workspaceId={currentWorkspaceId}
            canEdit={canEditWorkspaceContent}
            onOpenThread={handleJoinThread}
            refreshKey={decisions.length + todos.length + artifacts.length + shortLists.length}
          />
        }
      />
      {showTour && <OnboardingTour userId={user.id} />}
      {searchEl}
    </>
  );
}

export default App;
